<template>
  <div class="min-h-screen bg-bochas-bg px-4 py-6 sm:py-8">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8">
        <div>
          <h1 class="text-2xl sm:text-3xl font-bold text-white">
            <template v-if="isClubAdminValue">Mi club</template>
            <template v-else-if="isAsociacionAdminValue">Mis clubes</template>
            <template v-else>Clubes</template>
          </h1>
          <p class="text-sm text-gray-400 mt-1">
            <template v-if="isClubAdminValue">Editá la información de tu club.</template>
            <template v-else-if="isAsociacionAdminValue">
              Gestioná los clubes afiliados a tu federación.
            </template>
            <template v-else>
              Gestioná todos los clubes asociados a las asociaciones.
            </template>
          </p>
        </div>
        <div class="flex w-full gap-2 sm:w-auto">
          <button
            v-if="canDeleteAll && clubes.length"
            type="button"
            class="inline-flex min-h-11 flex-1 items-center justify-center rounded-xl border border-red-400/25 bg-red-500/10 px-4 text-sm font-semibold text-red-200 transition-colors hover:bg-red-500/20 disabled:cursor-wait disabled:opacity-60 sm:flex-none"
            :disabled="deletingAll"
            @click="onDeleteAll"
          >
            {{ deletingAll ? "Eliminando…" : "Eliminar todos" }}
          </button>
          <router-link
            v-if="canCreate"
            to="/admin/clubes/nueva"
            class="btn-bochas-rojo w-full text-center text-sm sm:w-auto"
          >
            + Nuevo club
          </router-link>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-12">
        <div
          class="inline-block w-10 h-10 sm:w-12 sm:h-12 border-4 border-bochas-rojo border-t-transparent rounded-full animate-spin">
        </div>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="bg-bochas-rojo/20 border border-bochas-rojo text-red-200 rounded-lg p-4">
        {{ error }}
      </div>

      <!-- Empty -->
      <div v-else-if="clubes.length === 0" class="text-center py-12">
        <p class="text-gray-400 mb-4">
          <template v-if="isClubAdminValue">No tenés un club asignado. Contactá a tu administrador.</template>
          <template v-else>Aún no hay clubes cargados.</template>
        </p>
        <router-link
          v-if="canCreate"
          to="/admin/clubes/nueva"
          class="btn-bochas-rojo inline-block"
        >
          Crear el primero
        </router-link>
      </div>

      <!-- Tabla desktop / Cards mobile -->
      <div v-else>
        <div class="mb-4 grid grid-cols-1 gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-3 md:grid-cols-4">
          <input
            v-model="search"
            type="search"
            class="rounded-lg border border-white/10 bg-white/5 px-3 py-3 text-sm text-white placeholder-gray-500 outline-none transition-colors focus:border-bochas-rojo"
            placeholder="Buscar club"
          />
          <SelectField
            v-model="filters.asociacionId"
            :options="asociacionFilterOptions"
          />
          <SelectField
            v-model="filters.locality"
            :options="localityFilterOptions"
          />
          <SelectField
            v-model="filters.status"
            :options="statusFilterOptions"
          />
        </div>

        <div class="hidden md:block card-fbochas overflow-hidden">
          <table class="w-full">
            <thead class="bg-white/5 border-b border-white/10">
              <tr class="text-left text-xs uppercase tracking-wide text-gray-400">
                <th class="px-4 py-3">
                  <button type="button" class="table-sort-button" @click="setSort('name')">
                    Club <span>{{ sortIcon("name") }}</span>
                  </button>
                </th>
                <th class="px-4 py-3">
                  <button type="button" class="table-sort-button" @click="setSort('asociacion')">
                    Asociación <span>{{ sortIcon("asociacion") }}</span>
                  </button>
                </th>
                <th class="px-4 py-3">
                  <button type="button" class="table-sort-button" @click="setSort('locality')">
                    Localidad <span>{{ sortIcon("locality") }}</span>
                  </button>
                </th>
                <th class="px-4 py-3">
                  <button type="button" class="table-sort-button" @click="setSort('status')">
                    Estado <span>{{ sortIcon("status") }}</span>
                  </button>
                </th>
                <th v-if="canViewRecordHistory" class="px-4 py-3">Registro</th>
                <th class="px-4 py-3 text-right space-x-2">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-white/5">
              <tr v-for="c in filteredClubes" :key="c.id" class="hover:bg-white/5 transition-colors">
                <td class="px-4 py-3">
                  <div class="flex items-center gap-3">
                    <div class="flex-shrink-0 w-10 h-10 rounded-full overflow-hidden bg-bochas-bg flex items-center justify-center">
                      <img v-if="c.logoUrl" :src="c.logoUrl" :alt="c.name" class="w-full h-full object-cover" />
                      <div v-else class="w-full h-full bg-degradado-bochas flex items-center justify-center text-white text-sm font-bold">
                        {{ initials(c.name) }}
                      </div>
                    </div>
                    <div class="min-w-0">
                      <div class="text-white font-medium truncate">{{ c.name }}</div>
                      <div v-if="c.foundationYear" class="text-xs text-gray-500">Fundado en {{ c.foundationYear }}</div>
                    </div>
                  </div>
                </td>
                <td class="px-4 py-3 text-sm text-gray-300">
                  {{ asociacionName(c.asociacionId) }}
                </td>
                <td class="px-4 py-3 text-sm text-gray-300">{{ c.locality || "—" }}</td>
                <td class="px-4 py-3">
                  <span :class="[
                    'text-xs px-2 py-1 rounded',
                    c.status === 'published'
                      ? 'bg-green-500/20 text-green-300'
                      : 'bg-yellow-500/20 text-yellow-300',
                  ]">
                    {{ c.status === 'published' ? 'Publicado' : 'Borrador' }}
                  </span>
                </td>
                <td v-if="canViewRecordHistory" class="px-4 py-3"><AdminRecordDates v-if="canViewRecordHistory" resource="clubs" :record="c" /></td>
                <td class="px-4 py-3 text-right">
                  <router-link
                    v-if="canEdit"
                    :to="`/admin/clubes/editar/${c.id}`"
                    class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-sky-400/25 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20"
                   title="Editar" aria-label="Editar"><IconFbochas name="edit" :size="16" /></router-link>
                  <button
                    v-if="canDelete"
                    @click="onDelete(c)"
                    class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-red-400/25 bg-red-500/10 text-red-300 hover:bg-red-500/20"
                   title="Eliminar" aria-label="Eliminar"><IconFbochas name="trash" :size="16" /></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="md:hidden space-y-3">
          <div v-for="c in filteredClubes" :key="c.id" class="card-fbochas p-4">
            <div class="flex items-start gap-3 mb-3">
              <div class="flex-shrink-0 w-12 h-12 rounded-full overflow-hidden bg-bochas-bg flex items-center justify-center">
                <img v-if="c.logoUrl" :src="c.logoUrl" :alt="c.name" class="w-full h-full object-cover" />
                <div v-else class="w-full h-full bg-degradado-bochas flex items-center justify-center text-white font-bold">
                  {{ initials(c.name) }}
                </div>
              </div>
              <div class="flex-1 min-w-0">
                <h3 class="text-white font-semibold truncate">{{ c.name }}</h3>
                <p class="text-xs text-gray-400">{{ asociacionName(c.asociacionId) }}</p>
                <p v-if="c.locality" class="text-xs text-gray-500">{{ c.locality }}</p>
              </div>
              <span :class="[
                'text-xs px-2 py-1 rounded flex-shrink-0',
                c.status === 'published'
                  ? 'bg-green-500/20 text-green-300'
                  : 'bg-yellow-500/20 text-yellow-300',
              ]">
                {{ c.status === 'published' ? 'Pub' : 'Borr' }}
              </span>
            </div>
            <AdminRecordDates v-if="canViewRecordHistory" resource="clubs" :record="c" class="mb-3" />
            <div class="flex items-center justify-end gap-2">
              <router-link
                v-if="canEdit"
                :to="`/admin/clubes/editar/${c.id}`"
                class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-sky-400/25 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20"
               title="Editar" aria-label="Editar"><IconFbochas name="edit" :size="16" /></router-link>
              <button
                v-if="canDelete"
                @click="onDelete(c)"
                class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-red-400/25 bg-red-500/10 text-red-300 hover:bg-red-500/20"
               title="Eliminar" aria-label="Eliminar"><IconFbochas name="trash" :size="16" /></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>


import IconFbochas from "@/components/Icons/IconFbochas.vue";
import AdminRecordDates from "@/components/Admin/AdminRecordDates.vue";
import { ref, computed, onMounted } from "vue";
import SelectField from "@/components/Common/SelectField.vue";
import { getStoredUser } from "@/services/auth/authService";
import {
  isBochasAdmin,
  isAsociacionAdmin,
  isClubAdmin,
  isDeveloper,
  isGeneralAdmin,
} from "@/utils/roles";
import {
  listarClubesAdmin,
  eliminarClub,
  eliminarClubes,
} from "@/services/clubes/clubesService";
import { listarAsociacionesAdmin } from "@/services/asociaciones/asociacionesService";
import { registrarInstitucionesResultados } from "@/services/tournaments/tournamentsService";
import { useConfirm } from "@/composables/useConfirm";

const canViewRecordHistory = computed(() => isGeneralAdmin(getStoredUser()));

const user = ref(getStoredUser());
const { confirm } = useConfirm();

const clubes = ref([]);
const asociaciones = ref([]);
const loading = ref(true);
const deletingAll = ref(false);
const error = ref("");
const search = ref("");
const filters = ref({
  asociacionId: "",
  locality: "",
  status: "",
});
const sortBy = ref("name");
const sortDirection = ref("asc");

const isDev = computed(() => isDeveloper(user.value));
const isBochasAdminValue = computed(() => isBochasAdmin(user.value));
const isAsociacionAdminValue = computed(() => isAsociacionAdmin(user.value));
const isClubAdminValue = computed(() => isClubAdmin(user.value));

// BOCHAS_ADMIN es read-only sobre clubes (solo ve). Puede crear solo el dev
// o el Asociación_ADMIN (de su fed). CLUB_ADMIN tampoco crea.
const canCreate = computed(
  () => isDev.value || isAsociacionAdminValue.value,
);
// BOCHAS_ADMIN y CLUB_ADMIN no eliminan. Solo dev o Asociación_ADMIN (de su fed).
const canDelete = computed(
  () => isDev.value || isAsociacionAdminValue.value,
);
const canDeleteAll = computed(() => isGeneralAdmin(user.value));
// BOCHAS_ADMIN no edita. Dev, Asociación_ADMIN (su fed) y CLUB_ADMIN (su club) sí.
const canEdit = computed(
  () => isDev.value || isAsociacionAdminValue.value || isClubAdminValue.value,
);

function initials(name) {
  if (!name) return "";
  return name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
}

function asociacionName(id) {
  if (id === null || id === undefined || id === "") return "—";
  const asociacion = asociaciones.value.find(
    (item) => String(item.id) === String(id),
  );
  return asociacion?.name || "Asociación no disponible";
}

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

const localityOptions = computed(() => {
  const collator = new Intl.Collator("es", { sensitivity: "base", numeric: true });
  return [...new Set(clubes.value.map((club) => club.locality).filter(Boolean))].sort(collator.compare);
});
const asociacionFilterOptions = computed(() => [
  { value: "", label: "Todas las asociaciones" },
  ...asociaciones.value.map((a) => ({ value: String(a.id), label: a.name })),
]);
const localityFilterOptions = computed(() => [
  { value: "", label: "Todas las localidades" },
  ...localityOptions.value.map((locality) => ({ value: locality, label: locality })),
]);
const statusFilterOptions = [
  { value: "", label: "Todos los estados" },
  { value: "published", label: "Publicado" },
  { value: "draft", label: "Borrador" },
];

const filteredClubes = computed(() => {
  const collator = new Intl.Collator("es", { sensitivity: "base", numeric: true });
  const term = normalizeText(search.value);

  return clubes.value
    .filter((club) => {
      const association = asociacionName(club.asociacionId);
      const matchesSearch = !term || normalizeText([
        club.name,
        association,
        club.locality,
        club.status,
      ].join(" ")).includes(term);

      return (
        matchesSearch &&
        (!filters.value.asociacionId || String(club.asociacionId) === filters.value.asociacionId) &&
        (!filters.value.locality || club.locality === filters.value.locality) &&
        (!filters.value.status || club.status === filters.value.status)
      );
    })
    .sort((a, b) => {
      let result = 0;
      if (sortBy.value === "asociacion") result = collator.compare(asociacionName(a.asociacionId), asociacionName(b.asociacionId));
      else result = collator.compare(String(a[sortBy.value] || ""), String(b[sortBy.value] || ""));
      return sortDirection.value === "asc" ? result : -result;
    });
});

function setSort(key) {
  if (sortBy.value === key) {
    sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
    return;
  }
  sortBy.value = key;
  sortDirection.value = "asc";
}

function sortIcon(key) {
  if (sortBy.value !== key) return "↕";
  return sortDirection.value === "asc" ? "↑" : "↓";
}

async function load() {
  loading.value = true;
  error.value = "";
  try {
    if (isDev.value || isBochasAdminValue.value) await registrarInstitucionesResultados();
    // Cargamos clubes y asociaciones en paralelo.
    // El backend ya filtra clubes por rol (BOCHAS_ADMIN ve todos, Asociación_ADMIN ve los suyos, CLUB_ADMIN ve solo el suyo).
    const [clubesRes, asociacionesRes] = await Promise.all([
      listarClubesAdmin(),
      isBochasAdminValue.value || isAsociacionAdminValue.value
        ? listarAsociacionesAdmin()
        : Promise.resolve({ items: [] }),
    ]);
    clubes.value = clubesRes.items || [];
    asociaciones.value = asociacionesRes.items || [];
  } catch (err) {
    error.value = err.response?.data?.message || err.message || "Error al cargar.";
  } finally {
    loading.value = false;
  }
}

async function onDelete(c) {
  const confirmed = await confirm({
    title: "Eliminar club",
    message: `¿Eliminar el club "${c.name}"? Esta acción no se puede deshacer.`,
  });
  if (!confirmed) return;
  try {
    await eliminarClub(c.id);
    await load();
  } catch (err) {
    error.value = err.response?.data?.message || err.message || "No se pudo eliminar.";
  }
}

async function onDeleteAll() {
  const total = clubes.value.length;
  const confirmed = await confirm({
    title: "Eliminar todos los clubes",
    message: `¿Eliminar los ${total} clubes cargados? Esta acción no se puede deshacer.`,
  });
  if (!confirmed) return;

  deletingAll.value = true;
  error.value = "";
  try {
    const result = await eliminarClubes(clubes.value.map((club) => club.id));
    await load();
    if (result.failed.length) {
      error.value = `Se eliminaron ${result.deleted} clubes. No se pudieron eliminar ${result.failed.length}.`;
    }
  } catch (err) {
    error.value = err.response?.data?.message || err.message || "No se pudieron eliminar los clubes.";
  } finally {
    deletingAll.value = false;
  }
}

onMounted(load);
</script>

<style scoped>
.table-sort-button {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  text-transform: uppercase;
  transition: color 160ms ease;
}

.table-sort-button:hover {
  color: white;
}
</style>
